import { GenericEntity, GithubRepoEntity } from "@cohesive/api-entities";
import { EntityCardSkeleton, type Entity } from "@cohesive/ui-components";
import { FolderGit2 } from "lucide-react";
import React, { useMemo } from "react";
import { EntityDragRow } from "../../../dnd/EntityDragRow";
import { SelectorEmpty } from "../../Selector";
import { CompactListItem } from "../components/CompactListItem";

interface ReposListViewProps {
  repos: GithubRepoEntity[];
  isLoading: boolean;
  onRepoSelect: (repo: GithubRepoEntity) => void;
  // Placement callback: receives the generic projection when available.
  onAddToContext: (entity: Entity) => void;
  genericById: Map<string, GenericEntity>;
}

export const ReposListView: React.FC<ReposListViewProps> = ({
  repos,
  isLoading,
  onRepoSelect,
  onAddToContext,
  genericById,
}) => {
  const repoIds = useMemo(() => repos.map((r) => String(r.data.id)), [repos]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <EntityCardSkeleton
            key={i}
            className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
          />
        ))}
      </div>
    );
  }

  if (repos.length === 0) {
    return <SelectorEmpty message="No repositories found" />;
  }

  return (
    <div>
      {repos.map((repo: GithubRepoEntity) => {
        const generic = genericById.get(String(repo.data.id));
        return (
          <EntityDragRow
            key={repo.data.id}
            entity={repo}
            generic={generic}
            entityId={String(repo.data.id)}
            orderedIds={repoIds}
            onSelect={() => onAddToContext(generic ?? repo)}
            dragScope="github"
          >
            <CompactListItem
              icon={<FolderGit2 className="h-4 w-4" />}
              title={repo.data.full_name}
              subtitle={repo.data.description || "No description"}
              webLink={repo.data.web_link}
              onClick={() => onRepoSelect(repo)}
              onAddToContext={() => onAddToContext(generic ?? repo)}
            />
          </EntityDragRow>
        );
      })}
    </div>
  );
};
